import {
    ArrowLeftOutlined,
    FileOutlined,
    FolderOutlined,
    PlusOutlined,
    UploadOutlined
} from '@ant-design/icons';
import {
    Button,
    Card,
    Col,
    Input,
    message,
    Modal,
    Row,
    Table,
    Typography,
    Upload
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
    createFolder,
    fetchFolderContents,
    uploadFile
} from '../../redux/rtk/features/document/documentSlice';
import getUserFromToken from "../../utils/getUserFromToken";

const { Title, Text } = Typography;

const FolderContents = () => {
    const { folderName } = useParams();
    const { folderId } = useParams();

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userId = getUserFromToken();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");

    const folderData = useSelector((state) => state.document?.folderData || []);
    const folders = useSelector((state) => state.document?.folders || []);

    // Find current folder's permissions
    const currentFolder = folders.find(
        folder => folder?.folder?.folderName === folderName
    );
    const hasEditPermission = currentFolder?.editFolderUserId === userId;

    useEffect(() => {
        if (folderName) {
            dispatch(fetchFolderContents(folderName));
        }
    }, [folderName, dispatch]);

    const handleFileUpload = async (file) => {
        if (!hasEditPermission) {
            message.error("You don't have permission to upload files");
            return;
        }

        try {
            await dispatch(uploadFile({ 
                folderName: folderName, 
                file 
            })).unwrap();
            
            message.success('File uploaded successfully');
            dispatch(fetchFolderContents(folderName));
        } catch (err) {
            message.error('File upload failed');
        }
    };

    const handleCreateFolder = async () => {
        if (newFolderName.trim() !== "") {
            try {
                const result = await dispatch(
                    createFolder({ 
                        folderName: newFolderName,
                        parentFolder: folderName,
                        userId: userId 
                    })
                ).unwrap();
                message.success(result.message);
                setNewFolderName("");
                setIsModalVisible(false);
                dispatch(fetchFolderContents(folderName));
            } catch (err) {
                message.error("Failed to create folder");
            }
        }
    };

    const handleViewFile = (fileName) => {
        navigate(`/admin/FolderManager/file/${fileName}`);
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => {
                const isFolder = !/\.[0-9a-z]+$/i.test(text);
                return (
                    <div 
                        style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            cursor: 'pointer' 
                        }}
                        onClick={() => isFolder ? 
                            navigate(`/admin/folder/${text}`) : 
                            handleViewFile(text)
                        }
                    >
                        {isFolder ? 
                            <FolderOutlined style={{ marginRight: 8, color: '#1890ff' }} /> : 
                            <FileOutlined style={{ marginRight: 8, color: '#666' }} />
                        }
                        <Text>{text}</Text>
                    </div>
                );
            },
        },
    ];

    const uploadProps = {
        customRequest: async ({ file }) => {
            handleFileUpload(file);
        },
        showUploadList: false,
    };

    return (
        <>
            <Card
                title={
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Button 
                                icon={<ArrowLeftOutlined />} 
                                onClick={() => navigate('/admin/FolderManager')}
                                style={{ marginRight: 16 }}
                            />
                            <Title level={4} style={{ display: 'inline-block', margin: 0 }}>
                                {/* Folder: {folderName} */}
                                Folder: {folderId}

                            </Title>
                        </Col>
                        {hasEditPermission && (
                            <Col>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsModalVisible(true)}
                                    style={{ marginRight: 16 }}
                                >
                                    Create Folder
                                </Button>
                                <Upload {...uploadProps}>
                                    <Button 
                                        type="primary" 
                                        icon={<UploadOutlined />}
                                    >
                                        Upload File
                                    </Button>
                                </Upload>
                            </Col>
                        )}
                    </Row>
                }
                bordered={false}
                style={{ 
                    width: '100%', 
                    maxWidth: 800, 
                    margin: '0 auto' 
                }}
            >
                <Table
                    dataSource={folderData.map((item) => ({
                        key: item,
                        name: item,
                    }))}
                    columns={columns}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                    }}
                />
            </Card>

            <Modal
                title="Create New Folder"
                visible={isModalVisible}
                onOk={handleCreateFolder}
                onCancel={() => setIsModalVisible(false)}
                okText="Create"
                cancelText="Cancel"
            >
                <Input
                    placeholder="Enter folder name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                />
            </Modal>
        </>
    );
};

export default FolderContents;